import { z } from 'zod';
import { MediaTypeEnum } from './media-unit-variants.js';
import { endpoint } from '../api/endpoint.js';
import { ReservationStatusSchema } from './dashboard.js';
import {
  sharedMediaUnitFilterFields,
  validateSharedMediaUnitFilterRefinements,
} from './media-unit-filter-fields.js';

// ─── Map pin (lightweight coordinate + status shape) ──────────

export const MapPinSchema = z.object({
  id: z.number(),
  latitude: z.number(),
  longitude: z.number(),
  mediaType: MediaTypeEnum.nullable(),
  status: ReservationStatusSchema,
});

export type MapPin = z.infer<typeof MapPinSchema>;

// ─── Response schema ──────────────────────────────────────────

export const MapPinsResponseSchema = z.object({
  data: z.array(MapPinSchema),
});

export type MapPinsResponse = z.infer<typeof MapPinsResponseSchema>;

// ─── Query schema ─────────────────────────────────────────────

/**
 * Filter shape for map pins. Shares field definitions with MediaUnitFiltersSchema
 * via sharedMediaUnitFilterFields to avoid duplication — including the numeric
 * range dimensions (area/width/height/audience/cost), material/multiformat/
 * tags/address/notes/roadNumber, noPhoto, and availabilityFrom/To
 * (map-catalog-filter-parity: these dimensions intentionally widen the map
 * contract to match the catalog).
 */
export const MapPinsQuerySchema = z
  .object({
    ...sharedMediaUnitFilterFields,
    status: z.array(ReservationStatusSchema).min(1).optional(),
    /**
     * Multi-column free-text search term. Matches the catalog search: a single
     * ILIKE over address/region/county/city/supplier_number/grw_id. Scalar (not
     * an array), so it is not coerced into an array by the map-pins controller.
     */
    search: z.string().optional(),
  })
  .superRefine(validateSharedMediaUnitFilterRefinements);

export type MapPinsQuery = z.infer<typeof MapPinsQuerySchema>;

// ─── Endpoint definition ──────────────────────────────────────

export const mapPinsEndpoint = endpoint(
  'GET',
  '/media-units/map-pins',
  MapPinsQuerySchema,
  MapPinsResponseSchema,
);
